import { useSyncExternalStore } from "react";

export type MoodKind =
  | "idle" | "thinking" | "responding" | "done" | "error"
  | "confirmation" | "listening" | "transcribing"
  | "office-attention" | "look-at" | "speaking";

export interface MochiSignal {
  kind: MoodKind;
  at?: { x: number; y: number };
  intensity?: number;
}

export const MOOD_PRIORITY: Record<MoodKind, number> = {
  error: 100,
  confirmation: 90,
  thinking: 80,
  responding: 78,
  transcribing: 72,
  listening: 70,
  speaking: 60,
  "office-attention": 50,
  done: 45,
  "look-at": 30,
  idle: 0,
};

export const IDLE_COOLDOWN_MS = 1500;

/** Pure: given the current and an incoming mood, which should be shown now.
 *  A transient ("done"/"look-at"/"office-attention") never clings; a
 *  higher-priority incoming always wins; a lower incoming is ignored while a
 *  higher is current. Returning to idle is driven by the cooldown, not here. */
export function nextMood(current: MoodKind, incoming: MoodKind): MoodKind {
  return MOOD_PRIORITY[incoming] >= MOOD_PRIORITY[current] ? incoming : current;
}

type Snapshot = { mood: MoodKind; at: { x: number; y: number } | null };
let snapshot: Snapshot = { mood: "idle", at: null };
const listeners = new Set<() => void>();
let cooldownTimer: ReturnType<typeof setTimeout> | null = null;

function publish(next: Snapshot) {
  const unchanged = next.mood === snapshot.mood && next.at === snapshot.at;
  snapshot = next;
  if (unchanged) return;
  listeners.forEach((l) => l());
}

export function emitMochiSignal(sig: MochiSignal): void {
  const mood = nextMood(snapshot.mood, sig.kind);
  publish({ mood, at: sig.at ?? snapshot.at });
  // Cooldown reset must happen unconditionally, even when publish() no-ops the
  // listener notify above — this is what keeps a sustained mood alive via
  // re-assertion (see useSustainedMood in publishers.ts).
  if (cooldownTimer) clearTimeout(cooldownTimer);
  if (mood !== "idle") {
    cooldownTimer = setTimeout(() => publish({ mood: "idle", at: snapshot.at }), IDLE_COOLDOWN_MS);
  }
}

/** Clears the last-known pointer position so the rig's idle/sleep phase can
 *  advance again (see useAmbientPublisher's no-move timer). No-ops (via the
 *  publish-bail) when already null. */
export function clearMochiPointer(): void {
  if (snapshot.at !== null) publish({ mood: snapshot.mood, at: null });
}

export function useMochiMood(): Snapshot {
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => listeners.delete(cb); },
    () => snapshot,
    () => snapshot,
  );
}

/** test-only accessor */
export function getMoodForTest(): MoodKind { return snapshot.mood; }

/** test-only accessor */
export function getAtForTest(): { x: number; y: number } | null { return snapshot.at; }

/** test-only: subscribe directly to publish notifications, bypassing React,
 *  so tests can assert exact notify counts without render-timing flakiness. */
export function subscribeForTest(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
